import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  postApiIntegrationsProductItemsNorthmillItems,
  type InsertNorthmillIntegrationItemRequest,
  type InsertNorthmillIntegrationItemsRequest,
  type ProductItemIntegrationResponse,
} from "apiClients/base-generated";
import { getHeyApiRequestOptions, normalizeHeyApiLang } from "apiClients/base/heyApiRequestOptions";

function heyApiResultToError(err: unknown): Error {
  const message = typeof err === "string" ? err : JSON.stringify(err);
  return new Error(message);
}

/** Bygger en fullständig Northmill-radbegäran från API-svar, med valfria överlagringar (t.ex. lås). */
export function northmillItemRequestFromRecord(
  record: ProductItemIntegrationResponse,
  overrides: Partial<InsertNorthmillIntegrationItemRequest> = {},
): InsertNorthmillIntegrationItemRequest {
  const itemNo = (overrides.itemNo !== undefined ? overrides.itemNo : record.itemNo)?.trim();
  if (!itemNo) {
    throw new Error("Saknar artikelnummer.");
  }

  const northmillGroupId =
    overrides.northmillGroupId !== undefined
      ? overrides.northmillGroupId
      : record.northmillGroupId?.trim() || null;
  const northmillAccountCodeId =
    overrides.northmillAccountCodeId !== undefined
      ? overrides.northmillAccountCodeId
      : record.northmillAccountCodeId?.trim() || null;
  const northmillLockedQuantity =
    overrides.northmillLockedQuantity !== undefined
      ? overrides.northmillLockedQuantity
      : record.northmillLockedQuantity ?? null;
  const lockedUntil =
    overrides.lockedUntil !== undefined ? overrides.lockedUntil : record.lockedUntil ?? null;
  const isInactive =
    overrides.isInactive !== undefined ? overrides.isInactive : Boolean(record.isInactive);

  return {
    itemNo,
    isInactive,
    northmillGroupId,
    northmillAccountCodeId,
    northmillLockedQuantity,
    lockedUntil,
  };
}

/** Skapar eller uppdaterar Northmill-integrationssynkrader per artikelnummer (`post .../northmill/items`). */
export function useIntegrationNorthmillUpsertMutation(locale: string | null | undefined) {
  const queryClient = useQueryClient();
  const lang = normalizeHeyApiLang(locale);

  return useMutation({
    mutationFn: async (body: InsertNorthmillIntegrationItemsRequest) => {
      const rawItems = body.items ?? [];
      const items = rawItems.filter((i) => (i?.itemNo ?? "").trim().length > 0);
      if (!items.length) {
        throw new Error("Saknar artikelnummer för minst en rad.");
      }
      const result = await postApiIntegrationsProductItemsNorthmillItems({
        ...getHeyApiRequestOptions(lang),
        body: { items },
      });
      if (result.error) {
        throw heyApiResultToError(result.error);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["integration-products"] });
    },
  });
}
